import React, { useState } from 'react';
import {
  Shield,
  ShieldCheck,
  AlertTriangle,
  Loader2,
  Key,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import adminOpsService from '../../services/adminOpsService';

/**
 * TwoFactorModal - Step-up 2FA verification for critical admin actions
 * 
 * Props:
 * - open: boolean - Controls dialog visibility
 * - onClose: () => void - Called when dialog should close
 * - onVerified: (token: string, reason?: string) => void - Called with action token after successful verification
 * - action: string - The action type requiring verification (e.g., 'intake_promote')
 * - actionLabel: string - Human-readable action description
 * - targetId: string - ID of the target
 * - targetLabel: string - Human-readable target name
 * - reasonRequired: boolean - Whether a reason/comment is required
 * - authHeaders: object - Auth headers to use for the request
 */
const TwoFactorModal = ({
  open,
  onClose,
  onVerified,
  action,
  actionLabel = 'this action',
  targetId,
  targetLabel,
  reasonRequired = false,
  authHeaders = {},
}) => {
  const [code, setCode] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Get fresh auth headers - prefer prop, fallback to localStorage
  const getEffectiveAuthHeaders = () => {
    // If authHeaders prop has Authorization, use it
    if (authHeaders?.Authorization) {
      return authHeaders;
    }
    // Otherwise, try to get from localStorage as fallback
    const token = localStorage.getItem('fundelite_token');
    if (token) {
      return { Authorization: `Bearer ${token}` };
    }
    return {};
  };

  const handleVerify = async () => {
    if (!code || code.length < 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    if (reasonRequired && !reason.trim()) {
      setError('A reason is required for this action');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Get fresh auth headers
      const effectiveHeaders = getEffectiveAuthHeaders();
      
      if (!effectiveHeaders.Authorization) {
        setError('Authentication required. Please refresh the page and try again.');
        setLoading(false);
        return;
      }
      
      // Set auth headers on the service
      adminOpsService.setAuthHeaders(effectiveHeaders);
      
      // Verify 2FA and get action token
      const data = await adminOpsService.verify2FA(code, action, targetId);

      if (data.success && data.token) {
        toast.success('2FA verified successfully');
        onVerified(data.token, reason);
        handleClose();
      } else {
        setError(data.error || 'Verification failed');
      }
    } catch (err) {
      console.error('2FA verification error:', err);
      // Better error messages
      if (err.message?.includes('401')) {
        setError('Authentication expired. Please refresh the page and try again.');
      } else if (err.message?.includes('Invalid verification code') || err.message?.includes('Invalid code')) {
        setError('Invalid verification code. Please check your authenticator app and try again.');
      } else {
        setError(err.message || 'Verification failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setCode('');
    setReason('');
    setError('');
    onClose();
  };

  const handleCodeChange = (e) => {
    // Only allow digits and limit to 6 characters
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setCode(value);
    setError('');
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md bg-stone-900 border-slate-700">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white">
            <Shield className="text-amber-400" size={20} />
            Two-Factor Verification Required
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            This action requires additional verification to proceed.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Action Info */}
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="text-amber-400 shrink-0 mt-0.5" size={18} />
              <div>
                <p className="text-sm text-white font-medium">
                  You are about to: {actionLabel}
                </p>
                {targetLabel && (
                  <p className="text-xs text-slate-400 mt-1">
                    Target: {targetLabel}
                  </p>
                )}
                <p className="text-xs text-amber-400 mt-2">
                  This action affects protected system behavior and requires 2FA verification.
                </p>
              </div>
            </div>
          </div>

          {/* Code Input */}
          <div className="space-y-2">
            <Label className="text-slate-300">Verification Code</Label>
            <div className="relative">
              <Key className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
              <Input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="Enter 6-digit code"
                value={code}
                onChange={handleCodeChange}
                className="pl-10 bg-stone-800 border-slate-700 text-center text-lg tracking-widest font-mono"
                autoFocus
                maxLength={6}
                data-testid="2fa-code-input"
              />
            </div>
            <p className="text-xs text-slate-500">
              Enter the code from your authenticator app
            </p>
          </div>

          {/* Reason Input (if required) */}
          {reasonRequired && (
            <div className="space-y-2">
              <Label className="text-slate-300 flex items-center gap-2">
                Reason
                <span className="text-amber-400 text-xs">(Required)</span>
              </Label>
              <Textarea
                placeholder="Explain why this action is being performed..."
                value={reason}
                onChange={(e) => { setReason(e.target.value); setError(''); }}
                className="bg-stone-800 border-slate-700 min-h-[80px]"
              />
              <p className="text-xs text-slate-500">
                This will be recorded in the audit log
              </p>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 flex items-center gap-2">
              <AlertTriangle className="text-red-400 shrink-0" size={16} />
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button 
            variant="outline" 
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleVerify}
            disabled={loading || code.length < 6 || (reasonRequired && !reason.trim())}
            className="bg-amber-600 hover:bg-amber-700"
            data-testid="2fa-verify-btn"
          >
            {loading ? (
              <>
                <Loader2 size={14} className="mr-2 animate-spin" />
                Verifying...
              </>
            ) : (
              <>
                <ShieldCheck size={14} className="mr-2" />
                Verify & Proceed
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TwoFactorModal;
